import React from 'react';

/**
 * FilterPanel - Componente reutilizable para paneles de filtros y búsqueda
 *
 * Integra con usePaginatedEntity para manejar filtros de forma declarativa.
 *
 * @param {Object} props - Props del componente
 * @param {Object} props.filters - Objeto con valores actuales de filtros (ej: { search: '', estado: '' })
 * @param {Function} props.onFilterChange - Handler para cambios: (name, value) => void
 * @param {Array<Object>} props.filterConfig - Array de configuración de filtros a renderizar
 * @param {Array<Object>} [props.actionConfig] - Array de botones de acción declarativos.
 *   Cada objeto: label {string}, icon {string} (fa-*), variant {string} (primary|secondary|...),
 *   onClick {Function}, show {Function} (opcional, retorna boolean).
 * @param {React.ReactNode} [props.actions] - JSX adicional renderizado junto a actionConfig.
 *   Usar solo para casos que no encajan en actionConfig (ej: componentes custom).
 *
 * @example
 * const filterConfig = [
 *   { name: 'search', type: 'text', placeholder: 'Buscar...', icon: 'fa-search' },
 *   {
 *     name: 'estado',
 *     type: 'select',
 *     placeholder: 'Todos los estados',
 *     options: [
 *       { value: 'operativo', label: 'Operativo' },
 *       { value: 'en_mantenimiento', label: 'En Mantenimiento' }
 *     ]
 *   }
 * ];
 *
 * const actionConfig = [
 *   { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
 *   { label: 'Nuevo Equipo',   icon: 'fa-plus',       variant: 'primary',   onClick: handleCreate, show: () => hasPermission('equipos.create') }
 * ];
 *
 * <FilterPanel
 *   filters={filters}
 *   onFilterChange={handleFilterChange}
 *   filterConfig={filterConfig}
 *   actionConfig={actionConfig}
 * />
 */
function FilterPanel({ filters = {}, onFilterChange, filterConfig = [], actionConfig, actions }) {
  if (!filterConfig || filterConfig.length === 0) {
    return null;
  }

  // Separar campo de búsqueda de otros filtros
  const searchConfig = filterConfig.find(f => f.type === 'text' && f.name === 'search');
  const otherFilters = filterConfig.filter(f => !(f.type === 'text' && f.name === 'search'));

  /**
   * Verifica si un filtro tiene valor activo
   * @param {string} name - Nombre del filtro
   * @returns {boolean}
   */
  const isFilterActive = (name) => {
    const value = filters[name];
    return value !== '' && value !== null && value !== undefined;
  };

  return (
    <div className="filters-panel">
      {/* Primera fila: Búsqueda principal + acciones */}
      {(searchConfig || actionConfig?.length > 0 || actions) && (
        <div className="filters-panel-top">
          {searchConfig && (
            <div className={`search-box flex-1 ${isFilterActive(searchConfig.name) ? 'filter-active' : ''}`}>
              <i className={`fas ${searchConfig.icon || 'fa-search'} sb-icon-left`}></i>
              <input
                type="text"
                placeholder={searchConfig.placeholder || 'Buscar...'}
                value={filters[searchConfig.name] || ''}
                onChange={(e) => onFilterChange(searchConfig.name, e.target.value)}
                className={isFilterActive(searchConfig.name) ? 'filter-active' : ''}
              />
              {isFilterActive(searchConfig.name) && (
                <button
                  className="search-clear"
                  onClick={() => onFilterChange(searchConfig.name, '')}
                  title="Limpiar búsqueda"
                  aria-label="Limpiar búsqueda"
                  tabIndex={-1}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          )}
          {(actionConfig?.length > 0 || actions) && (
            <div className="filters-panel-actions">
              {actionConfig?.map((a, i) =>
                (!a.show || a.show()) ? (
                  <button
                    key={i}
                    className={`btn btn-${a.variant || 'secondary'}`}
                    onClick={a.onClick}
                  >
                    <i className={`fas ${a.icon}`}></i> {a.label}
                  </button>
                ) : null
              )}
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Segunda fila: Filtros adicionales (selects, etc.) */}
      {otherFilters.length > 0 && (
        <>
          <div className="filters-divider"></div>
          <div className="filters-panel-row">
            {otherFilters.map((filter) => (
              <React.Fragment key={filter.name}>
                {filter.type === 'select' && (
                  <select
                    value={filters[filter.name] || ''}
                    onChange={(e) => onFilterChange(filter.name, e.target.value)}
                    className={`form-select flex-1 ${isFilterActive(filter.name) ? 'filter-active' : ''}`}
                  >
                    <option value="">{filter.placeholder || 'Seleccionar...'}</option>
                    {filter.options?.map((option, optIndex) => (
                      <option key={optIndex} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                {filter.type === 'date' && (
                  <input
                    type="date"
                    value={filters[filter.name] || ''}
                    onChange={(e) => onFilterChange(filter.name, e.target.value)}
                    className={`form-control flex-1 ${isFilterActive(filter.name) ? 'filter-active' : ''}`}
                    placeholder={filter.label || filter.placeholder || ''}
                    title={filter.label || filter.placeholder || ''}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

    </div>
  );
}

export default FilterPanel;
